import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { ChatAxios } from "../../axios/chat/chat.axios";
import { CreateOrgChatMessageDto } from "../../model/chat/dto/CreateOrgChatMessageDto";
import { GetOrgChatMembersResponseDto } from "../../model/chat/dto/GetOrgChatMembersResponseDto";
import { GetOrgChatMessagesDto } from "../../model/chat/dto/GetOrgChatMessagesDto";
import { GetOrgChatMessagesResponseDto } from "../../model/chat/dto/GetOrgChatMessagesResponseDto";
import { GetOrgChatUnreadCountResponseDto } from "../../model/chat/dto/GetOrgChatUnreadCountResponseDto";
import { OrgChatMember } from "../../model/chat/OrgChatMember";
import { OrgChatMessage } from "../../model/chat/OrgChatMessage";
import { AtiraThunk } from "../AtiraThunk";
import { revertAllReduxAction } from "../store-reset";
import { userActions } from "../user/user.slice";

interface OrgChatReducer {
  messages: OrgChatMessage[];
  members: OrgChatMember[];
  hasMore: boolean;
  loading: boolean;
  sending: boolean;
  unreadCount: number;
}

const initialState = Object.freeze<OrgChatReducer>({
  messages: [],
  members: [],
  hasMore: false,
  loading: false,
  sending: false,
  unreadCount: 0,
});

const getMessages = AtiraThunk<
  GetOrgChatMessagesResponseDto,
  GetOrgChatMessagesDto
>("orgChat/getMessages", ChatAxios.getOrgChatMessages);

const sendMessage = AtiraThunk<OrgChatMessage, CreateOrgChatMessageDto>(
  "orgChat/sendMessage",
  ChatAxios.sendOrgChatMessage,
);

const getMembers = AtiraThunk<GetOrgChatMembersResponseDto, void>(
  "orgChat/getMembers",
  ChatAxios.getOrgChatMembers,
);

const getUnreadCount = AtiraThunk<GetOrgChatUnreadCountResponseDto, void>(
  "orgChat/getUnreadCount",
  ChatAxios.getOrgChatUnreadCount,
);

const markAsRead = AtiraThunk<void, void>(
  "orgChat/markAsRead",
  ChatAxios.markOrgChatAsRead,
);

const orgChatSlice = createSlice({
  name: "orgChat",
  initialState,
  reducers: {
    messageReceived: (state, action: { payload: OrgChatMessage }) => {
      if (state.messages.some((m) => m._id === action.payload._id)) {
        return;
      }

      state.messages.push(action.payload);
    },
    // A live push from someone else while the room isn't open - no round trip
    // for every message, only the mark-as-read call resyncs with the server.
    unreadIncremented: (state) => {
      state.unreadCount += 1;
    },
    unreadCleared: (state) => {
      state.unreadCount = 0;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getMessages.pending, (state) => {
      state.loading = true;
    });
    addCase(getMessages.fulfilled, (state, action) => {
      // Server returns newest-first pages; the list renders oldest-first.
      const page = [...action.payload.messages].reverse();

      if (action.meta.arg.beforeId) {
        // An older page: whatever it shares with the list is already there
        const existingIds = new Set(state.messages.map((m) => m._id));

        state.messages = [
          ...page.filter((m) => !existingIds.has(m._id)),
          ...state.messages,
        ];
        state.hasMore = action.payload.hasMore;
      } else {
        // The latest page is the source of truth, but the socket listener runs
        // even while this page is closed, so a push may be newer than anything
        // the server had when it answered. Ids sort by creation time.
        const newest = page[page.length - 1]?._id;
        const pageIds = new Set(page.map((m) => m._id));

        state.messages = [
          ...page,
          ...state.messages.filter(
            (m) => !pageIds.has(m._id) && (!newest || m._id > newest),
          ),
        ];
        state.hasMore = action.payload.hasMore;
      }

      state.loading = false;
    });
    addCase(getMessages.rejected, (state) => {
      state.loading = false;
    });

    addCase(sendMessage.pending, (state) => {
      state.sending = true;
    });
    addCase(sendMessage.fulfilled, (state, action) => {
      if (!state.messages.some((m) => m._id === action.payload._id)) {
        state.messages.push(action.payload);
      }

      state.sending = false;
    });
    addCase(sendMessage.rejected, (state) => {
      state.sending = false;
    });

    addCase(getMembers.fulfilled, (state, action) => {
      state.members = action.payload.members;
    });

    addCase(getUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload.unreadCount;
    });

    addCase(markAsRead.fulfilled, (state) => {
      state.unreadCount = 0;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const orgChatActions = {
  ...orgChatSlice.actions,
  getMessages,
  sendMessage,
  getMembers,
  getUnreadCount,
  markAsRead,
};

export default orgChatSlice.reducer;
